import { Filter } from "@axl-dev/school-sdk";
import { Form, Select } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLoc } from "../lib/hooks";

import { DropdownPopover } from "./DropdownPopover";
import { UtmTagSelect } from "./UtmTagSelect";

type UTMTagsSelectorValue = {
  source: string[];
  campaign: string[];
  content: string[];
  medium: string[];
  term: string[];
};

export declare type UtmTagType =
  | "source"
  | "medium"
  | "campaign"
  | "term"
  | "content";

export class UtmTagFilter extends Filter {
  constructor(filter?: Partial<UtmTagFilter>) {
    super();
    if (filter) this.update(filter);
  }

  type: UtmTagType;

  direction: "first" | "last";

  update(changes: Partial<UtmTagFilter>) {
    super.update(changes);
  }
}
type Props = {
  defaultValue?: UTMTagsSelectorValue;
  filter?: Omit<Partial<UtmTagFilter>, "type">;
  onChange: (changes: UTMTagsSelectorValue) => void;
  style?: React.CSSProperties;
};
export const UtmTagsSelector: React.FC<Props> = ({
  defaultValue,
  filter,
  style,
  onChange,
}) => {
  const loc = useLoc();
  const isTouched = useRef(false);
  const [source, setSource] = useState<string[]>(defaultValue?.source ?? []);
  const [campaign, setCampaign] = useState<string[]>(
    defaultValue?.campaign ?? [],
  );
  const [content, setContent] = useState<string[]>(defaultValue?.content ?? []);
  const [medium, setMedium] = useState<string[]>(defaultValue?.medium ?? []);
  const [term, setTerm] = useState<string[]>(defaultValue?.term ?? []);

  useEffect(() => {
    if (!isTouched.current) return;
    onChange({ source, campaign, content, medium, term });
  }, [source, campaign, content, medium, term]);

  const value = useMemo(() => {
    if (
      source.length === 0 &&
      campaign.length === 0 &&
      content.length === 0 &&
      medium.length === 0 &&
      term.length === 0
    )
      return null;

    return loc.word("UTMTagsSelector.valueLabel", {
      default: "UTM tags are selected",
    });
  }, [source, campaign, content, medium, term, loc]);

  const clearTags = useCallback(() => {
    setSource([]);
    setCampaign([]);
    setContent([]);
    setMedium([]);
    setTerm([]);
  }, []);

  return (
    <DropdownPopover
      transparent
      shadow
      onOpenChange={(visible) => {
        if (visible) isTouched.current = true;
      }}
      trigger={["click"]}
      content={
        <Form
          onClick={(e) => e.stopPropagation()}
          layout="vertical"
          className="bg-white w-100 p-10"
        >
          <Form.Item
            className="flex-1"
            label={loc.word("Analytics.conversionType.uTMSource", {
              default: "UTM Source",
            })}
          >
            <UtmTagSelect
              value={source}
              onChange={(x) => {
                setSource(x);
              }}
              filter={{ type: "source", ...filter }}
            />
          </Form.Item>

          <Form.Item
            className="flex-1"
            label={loc.word("Analytics.conversionType.uTMCampaign", {
              default: "UTM Campaign",
            })}
          >
            <UtmTagSelect
              value={campaign}
              onChange={(x) => setCampaign(x)}
              filter={{ type: "campaign", ...filter }}
            />
          </Form.Item>

          <Form.Item
            className="flex-1"
            label={loc.word("Analytics.conversionType.uTMContent", {
              default: "UTM Content",
            })}
          >
            <UtmTagSelect
              value={content}
              onChange={(x) => setContent(x)}
              filter={{ type: "content", ...filter }}
            />
          </Form.Item>

          <Form.Item
            className="flex-1"
            label={loc.word("Analytics.conversionType.uTMMedium", {
              default: "UTM Medium",
            })}
          >
            <UtmTagSelect
              value={medium}
              onChange={(x) => setMedium(x)}
              filter={{ type: "medium", ...filter }}
            />
          </Form.Item>

          <Form.Item
            className="flex-1"
            label={loc.word("Analytics.conversionType.uTMTerm", {
              default: "UTM Term",
            })}
          >
            <UtmTagSelect
              value={term}
              onChange={(x) => setTerm(x)}
              filter={{ type: "term", ...filter }}
            />
          </Form.Item>
        </Form>
      }
    >
      <Select
        value={value}
        allowClear
        onClear={clearTags}
        placeholder={loc.word("UTMTagsSelector.placeholder", {
          default: "Select UTM tags",
        })}
        open={false}
        style={style}
      />
    </DropdownPopover>
  );
};
